import React, { useState } from 'react';
import { Chip, CircularProgress, makeStyles } from '@material-ui/core';
import {
  COLORS, PERIODS, PERIOD_SUFFIX, getDateRange,
} from './data';
import resumenRequest from '../../../requests/api/resume';

const useStyles = makeStyles(() => ({
  panel: {
    background: 'white',
    borderRadius: 14,
    border: '1px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  header: {
    padding: '12px 16px 10px',
    borderBottom: `1px solid ${COLORS.mist}`,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  title: {
    fontSize: 13.5,
    fontWeight: 700,
    color: COLORS.ink,
  },
  chips: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  empty: {
    padding: '20px 16px',
    fontSize: 12.5,
    color: COLORS.slate,
    textAlign: 'center',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid #f2f5f7',
    gap: 10,
    '&:last-child': { borderBottom: 'none' },
  },
  wrap: { flex: 1, minWidth: 0 },
  name: {
    fontSize: 12.5,
    fontWeight: 600,
    color: COLORS.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: 3,
  },
  barBg: {
    height: 4,
    background: COLORS.mist,
    borderRadius: 10,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    background: `linear-gradient(90deg,${COLORS.orange},#FEA95C)`,
    borderRadius: 10,
  },
  amount: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.ink,
    textAlign: 'right',
    minWidth: 68,
  },
  orders: {
    fontSize: 10.5,
    color: COLORS.slate,
    fontWeight: 400,
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 16px',
  },
}));

const fmt = (n) => `$${n.toLocaleString('es-CL')}`;

export default function StoreSalesPanel({ ventasPorLocal: initialVentas, clientId }) {
  const classes = useStyles();
  const [period, setPeriod] = useState('today');
  const [ventasPorLocal, setVentasPorLocal] = useState(initialVentas);
  const [loading, setLoading] = useState(false);

  const handlePeriodChange = async (key) => {
    if (key === period) return;
    setPeriod(key);
    const { startDate, endDate } = getDateRange(key);
    setLoading(true);
    try {
      const response = await resumenRequest.getStoreSales(clientId, startDate, endDate);
      if (response.success) setVentasPorLocal(response.data);
    } finally {
      setLoading(false);
    }
  };

  const maxAmount = ventasPorLocal && ventasPorLocal.length > 0
    ? Math.max(...ventasPorLocal.map((s) => s.amount))
    : 0;

  const isEmpty = !ventasPorLocal || ventasPorLocal.length === 0 || maxAmount === 0;

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <span className={classes.title}>
            Ventas por local — {PERIOD_SUFFIX[period]}
          </span>
        </div>
        <div className={classes.chips}>
          {PERIODS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              size="small"
              onClick={() => handlePeriodChange(p.key)}
              color={period === p.key ? 'primary' : 'default'}
              variant={period === p.key ? 'default' : 'outlined'}
            />
          ))}
        </div>
      </div>
      {loading && (
        <div className={classes.loadingWrap}>
          <CircularProgress size={22} />
        </div>
      )}
      {!loading && isEmpty && (
        <div className={classes.empty}>No hay ventas para este período</div>
      )}
      {!loading && !isEmpty && ventasPorLocal.map((store) => (
        <div key={store.storeId} className={classes.row}>
          <div className={classes.wrap}>
            <div className={classes.name}>{store.name}</div>
            <div className={classes.barBg}>
              <div className={classes.barFill} style={{ width: `${(store.amount / maxAmount) * 100}%` }} />
            </div>
          </div>
          <div className={classes.amount}>
            {fmt(store.amount)}<br />
            <span className={classes.orders}>{store.orders} bol.</span>
          </div>
        </div>
      ))}
    </div>
  );
}
